import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { expenseApi, expensePaymentApi } from '../../services/api';
import { formatDate, formatDateRange } from '../../utils/dateUtils';

const ExpenseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [expense, setExpense] = useState(null);
  const [expenseStatus, setExpenseStatus] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch expense details
        const expenseResponse = await expenseApi.getById(id);
        setExpense(expenseResponse.data);
        
        // Fetch expense status
        const statusResponse = await expenseApi.getStatus(id);
        setExpenseStatus(statusResponse.data);
        
        // Fetch payment history
        const paymentsResponse = await expensePaymentApi.getByExpense(id);
        setPayments(paymentsResponse.data);
        
        setLoading(false);
      } catch (err) {
        setError('Failed to load expense data');
        setLoading(false);
        console.error(err);
      }
    };

    fetchData();
  }, [id]);

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this expense? This action cannot be undone.')) {
      try {
        await expenseApi.delete(id);
        navigate('/expenses');
      } catch (err) {
        setError('Failed to delete expense');
        console.error(err);
      }
    }
  };

  if (loading) {
    return <div className="text-center py-5"><i className="fas fa-spinner fa-spin fa-2x"></i></div>;
  }

  if (error) {
    return <div className="alert alert-danger"><i className="fas fa-exclamation-triangle me-2"></i>{error}</div>;
  }

  if (!expense) {
    return <div className="alert alert-warning"><i className="fas fa-exclamation-circle me-2"></i>Expense not found</div>;
  }

  return (
    <div className="container-fluid">
      <div className="row mb-4">
        <div className="col">
          <div className="d-flex justify-content-between align-items-center">
            <h1 className="display-5 mb-0">{expense.name}</h1>
            <div className="d-flex gap-2">
              <Link to={`/expenses/${id}/edit`} className="btn btn-outline-secondary">
                <i className="fas fa-edit me-2"></i>Edit
              </Link>
              <Link 
                to={`/expense-payments/new?expense_id=${id}&amount=${expenseStatus?.monthly_cost || ''}`} 
                className="btn btn-success"
              >
                <i className="fas fa-plus-circle me-2"></i>Add Record
              </Link>
              <button 
                onClick={handleDelete} 
                className="btn btn-outline-danger"
              >
                <i className="fas fa-trash-alt me-2"></i>Delete
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <div className="row">
        <div className="col-md-6 mb-4">
          <div className="card shadow-sm mb-4">
            <div className="card-header bg-white py-3">
              <h5 className="card-title mb-0">
                <i className="fas fa-info-circle me-2"></i>
                Expense Information
              </h5>
            </div>
            <div className="card-body p-0">
              <table className="table table-hover mb-0">
                <tbody>
                  <tr>
                    <th className="bg-light" style={{width: "30%"}}>Category</th>
                    <td>
                      <span className="badge bg-secondary rounded-pill">
                        {expense.category}
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <th className="bg-light">Interval</th>
                    <td>
                      <span className={`badge ${
                        expense.interval === 'monthly' ? 'bg-primary' : 
                        expense.interval === 'quarterly' ? 'bg-info' : 
                        expense.interval === 'yearly' ? 'bg-warning text-dark' : 
                        'bg-dark'
                      }`}>
                        {expense.interval}
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <th className="bg-light">Monthly Split</th>
                    <td>
                      {expense.monthly_split ? 
                        <span className="badge bg-success">Yes</span> : 
                        <span className="badge bg-light text-dark">No</span>
                      }
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          
          {expenseStatus && (
            <div className="card shadow-sm">
              <div className="card-header bg-white py-3">
                <h5 className="card-title mb-0">
                  <i className="fas fa-chart-pie me-2"></i>
                  Payment Status
                </h5>
              </div>
              <div className="card-body p-0">
                <table className="table table-hover mb-0">
                  <tbody>
                    <tr>
                      <th className="bg-light" style={{width: "30%"}}>Paid Until</th>
                      <td>
                        {expenseStatus.paid_until ? 
                          formatDate(expenseStatus.paid_until) : 
                          <span className="text-muted">N/A</span>
                        }
                      </td>
                    </tr>
                    <tr>
                      <th className="bg-light">Monthly Cost</th>
                      <td className="fw-bold text-danger">{expenseStatus.monthly_cost.toFixed(2)} €</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
        
        <div className="col-md-6">
          <div className="card shadow-sm">
            <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center">
              <h5 className="card-title mb-0">
                <i className="fas fa-history me-2"></i>
                Payment History
              </h5>
              <span className="badge bg-primary rounded-pill px-3 py-2">
                {payments.length} payments
              </span>
            </div>
            <div className="card-body p-0">
              {payments.length === 0 ? (
                <div className="alert alert-info m-3">
                  <i className="fas fa-info-circle me-2"></i>
                  No payments recorded yet.
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead >
                      <tr>
                        <th>Date</th>
                        <th>Amount</th>
                        <th>Period</th>
                        <th className="text-end" style={{ width: '1%', whiteSpace: 'nowrap' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payments.map(payment => (
                        <tr key={payment.id}>
                          <td>{formatDate(payment.paid_on)}</td>
                          <td className="fw-bold">{payment.amount.toFixed(2)} €</td>
                          <td>
                            {payment.valid_from && payment.valid_until
                              ? formatDateRange(payment.valid_from, payment.valid_until)
                              : <span className="text-muted">N/A</span>}
                          </td>
                          <td className="text-end">
                            <Link to={`/expense-payments/${payment.id}/edit`} className="btn btn-sm btn-outline-secondary">
                              <i className="fas fa-edit"></i>
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpenseDetail;
